'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ExternalLink,
  Info,
  ListPlus,
  Share2,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { AppLink } from '@/components/mui/app-link';
import FixedIncomeCompareDialog from '@/components/fixed-income/FixedIncomeCompareDialog';
import { useAppColors } from '@/styles/colors';

type Row = {
  id: string;
  idCem: string;
  name: string;
  titleType: string;
  remunerationType: string;
  indexer: string;
  cashFlowType: string;
  paymentFrequency: string;
  riskFactor: string;
  taxRegime: string;
  liquidity: string;
  quoteDate: string | null;
  conversionDate: string | null;
  maturityDate: string | null;
  sellRateMorning: number | null;
  sellPuMorning: number | null;
  daysToMaturity: number;
  durationYears: number | null;
  modifiedDurationYears: number | null;
  dv01: number | null;
  convexity: number | null;
  sensitivityShockBps: number;
  sensitivityUp25BpsPct: number | null;
  sensitivityDown25BpsPct: number | null;
  sensitivityMethod: string | null;
  riskMetricsStatus: string;
  riskMetricsUnavailableReason: string | null;
  source: string;
  sourceAsOf: string | null;
  cashFlowScheduleId: string | null;
};

type HistoryPoint = {
  date: string | null;
  sellPu: number | null;
  sellRate: number | null;
  sellPuBase100: number | null;
};
type History = { id: string; points: HistoryPoint[] };
type Benchmark = { value: number; date: string | null } | null;
type Benchmarks = { selic: Benchmark; ipca: Benchmark; igpm: Benchmark };
type ChartPoint = { x: number; label: string; value: number; unit?: 'BRL' | 'NORMALIZED' };
type ChartSeries = { label: string; color: string; points: ChartPoint[] };
type CashFlowPoint = {
  date: string | null;
  flowType: string;
  amount: number | null;
  indexer: string | null;
  projected: boolean;
};

const LABELS: Record<string, string> = {
  NONE: 'Sem indexador',
  IPCA: 'IPCA',
  IGPM: 'IGP-M',
  SELIC: 'Selic',
  SELIC_SPREAD: 'Spread sobre Selic',
  FIXED_RATE: 'Taxa fixa',
  FLOATING_RATE: 'Taxa flutuante',
  INDEX_PLUS_SPREAD: 'Índice + taxa',
  BULLET: 'Sem cupons',
  PERIODIC_COUPON: 'Juros semestrais',
  ANNUITY: 'Renda mensal',
  MATURITY: 'No vencimento',
  SEMIANNUAL: 'Semestral',
  MONTHLY: 'Mensal',
  NOMINAL_RATE: 'Taxa nominal',
  REAL_RATE: 'Taxa real',
  IGPM_RATE: 'Taxa IGP-M',
  REGRESSIVE_IR_IOF: 'Regressiva',
  TREASURY_DAILY_REPURCHASE: 'D+0 / D+1 útil',
  tesouro_transparente: 'Tesouro Transparente',
  'IGPM+ COM JUROS SEMESTRAIS': 'IGP-M+ com juros semestrais',
  'IPCA+ COM JUROS SEMESTRAIS': 'IPCA+ com juros semestrais',
  PREFIXADO: 'Prefixado',
  'PREFIXADO COM JUROS SEMESTRAIS': 'Prefixado com juros semestrais',
  COUPON: 'Cupom',
  PRINCIPAL: 'Principal',
  AMORTIZATION: 'Amortização',
};
const RISK_FACTOR_DESCRIPTIONS: Record<string, string> = {
  NOMINAL_RATE: 'A variação é aplicada à taxa nominal publicada do título.',
  REAL_RATE: 'A variação é aplicada à taxa real publicada, sem projetar IPCA ou IGP-M.',
  IGPM_RATE: 'A variação é aplicada à taxa publicada associada ao IGP-M.',
  SELIC_SPREAD: 'A variação é aplicada ao spread publicado sobre a Selic, não à Selic efetiva.',
};
const formatEnum = (value: string | null | undefined) =>
  value ? (LABELS[value] ?? value.replaceAll('_', ' ').toLowerCase()) : '—';
const riskFactorDescription = (value: string) =>
  RISK_FACTOR_DESCRIPTIONS[value] ?? 'A variação é aplicada ao fator de risco informado para o título.';
const dateFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' });
const formatDate = (value: string | null) => (value ? dateFormatter.format(new Date(`${value}T12:00:00Z`)) : '—');
const formatNumber = (value: number | null, digits = 2) =>
  value === null ? '—' : value.toLocaleString('pt-BR', { maximumFractionDigits: digits });
const formatPrice = (value: number | null) =>
  value === null ? '—' : value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDv01 = (value: number | null) =>
  value === null
    ? '—'
    : value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
const isIndexedFlow = (indexer: string | null) => indexer === 'IPCA' || indexer === 'IGPM';
const normalizeTitleDisplay = (value: string) => value.replace(/\b(20\d{2})-\d{2}-\d{2}\b/g, '$1');

const titleTag = (row: Row) => {
  const normalized = `${row.titleType} ${row.indexer}`.toUpperCase();
  if (normalized.includes('IPCA')) return 'IPCA+';
  if (normalized.includes('SELIC')) return 'Selic';
  if (normalized.includes('PREFIXADO')) return 'Prefixado';
  if (normalized.includes('IGPM')) return 'IGP-M+';
  return formatEnum(row.titleType);
};
const friendlyTitle = (row: Row) => {
  const rawPrefix = row.titleType.trim();
  const rawName = row.name.toUpperCase();
  const prefix = rawName.startsWith(`TESOURO ${rawPrefix.toUpperCase()}`) ? `TESOURO ${rawPrefix}` : rawPrefix;
  return rawName.startsWith(prefix.toUpperCase())
    ? normalizeTitleDisplay(`Tesouro ${formatEnum(rawPrefix)}${row.name.slice(prefix.length)}`)
    : normalizeTitleDisplay(row.name);
};
const instrumentCode = (row: Row) => {
  const seriesDate = row.conversionDate ?? row.maturityDate;
  const year = seriesDate?.slice(2, 4) ?? row.name.match(/20(\d{2})/)?.[1] ?? '';
  const fullYear = seriesDate?.slice(0, 4) ?? '';
  const normalized = row.titleType.toUpperCase();
  if (normalized.includes('IPCA')) return `NTNB${year}`;
  if (normalized.includes('SELIC')) return `LFT${fullYear}`;
  if (normalized.includes('PREFIXADO')) return `LTN${fullYear}`;
  if (normalized.includes('IGPM')) return `NTNC${year}`;
  if (normalized.includes('RENDA')) return `RENDA+${fullYear}`;
  if (normalized.includes('EDUCA')) return `EDUCA+${fullYear}`;
  return row.idCem;
};
const compactChartTitle = (row: Row) => {
  const year = row.conversionDate?.slice(0, 4) ?? row.name.match(/20\d{2}/)?.[0] ?? row.maturityDate?.slice(0, 4) ?? '';
  const coupon = row.cashFlowType === 'PERIODIC_COUPON' ? ' + juros' : '';
  return `${titleTag(row)} ${year}${coupon}`.trim();
};
const liquidityLabel = (row: Row) => {
  if (row.liquidity === 'TREASURY_DAILY_REPURCHASE')
    return row.indexer === 'IPCA' || row.indexer === 'IGPM' ? 'D+1 útil' : 'D+0';
  if (['D+0', 'D0', 'SAME_DAY'].includes(row.liquidity)) return 'D+0';
  if (['D+1', 'D1', 'NEXT_BUSINESS_DAY'].includes(row.liquidity)) return 'D+1 útil';
  return formatEnum(row.liquidity);
};
const formatRate = (row: Row) => {
  if (row.sellRateMorning === null) return '—';
  const rate = `${formatNumber(row.sellRateMorning, 3)}%`;
  if (row.indexer === 'IPCA') return `${rate} + IPCA`;
  if (row.indexer === 'SELIC') return `Selic + ${rate}`;
  if (row.indexer === 'IGPM') return `${rate} + IGP-M`;
  return rate;
};

function MissingValue({ reason = 'Não calculável com os dados disponíveis para este título.' }: { reason?: string }) {
  return (
    <Tooltip title={reason} arrow>
      <Box component='span' sx={{ whiteSpace: 'nowrap', cursor: 'help' }}>
        —{' '}
        <Typography component='span' variant='caption' color='text.secondary'>
          ⓘ
        </Typography>
      </Box>
    </Tooltip>
  );
}

function IndicatorLabel({ label, description }: { label: string; description: string }) {
  return (
    <Stack direction='row' spacing={0.6} alignItems='center' sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ fontWeight: 650, lineHeight: 1.25 }}>
        {label}
      </Typography>
      <Tooltip title={description} arrow placement='top'>
        <Box component='span' sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'help' }}>
          <CircleHelp size={14} />
        </Box>
      </Tooltip>
    </Stack>
  );
}

function SectionHeader({
  id,
  title,
  count,
  collapsed,
  onToggle,
}: {
  id: string;
  title: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      component='button'
      type='button'
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-controls={id}
      sx={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: 0.8,
        width: '100%',
        px: 1.5,
        py: 1,
        border: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        color: 'text.primary',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      {collapsed ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
      <Typography variant='body2' sx={{ fontWeight: 750 }}>
        {title}
      </Typography>
      <Chip size='small' label={count} sx={{ height: 20, fontSize: 11, fontWeight: 750 }} />
    </Box>
  );
}

function ChartCard({
  id,
  title,
  subtitle,
  children,
}: {
  id?: string;
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <Paper
      id={id}
      variant='outlined'
      sx={{
        p: 1.5,
        minWidth: 0,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant='body2' sx={{ fontWeight: 750 }}>
        {title}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {subtitle}
      </Typography>
      <Box sx={{ mt: 1, width: '100%', overflow: 'hidden' }}>{children}</Box>
    </Paper>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <Box
      sx={{
        minHeight: 190,
        display: 'grid',
        placeItems: 'center',
        color: 'text.secondary',
        textAlign: 'center',
        px: 3,
      }}
    >
      <Typography variant='body2'>{message}</Typography>
    </Box>
  );
}

function LineChart({
  series,
  xLabel,
  yLabel,
  percent = false,
  xTickFormatter,
}: {
  series: ChartSeries[];
  xLabel: string;
  yLabel: string;
  percent?: boolean;
  xTickFormatter?: (value: number) => string;
}) {
  const points = series.flatMap((item) => item.points);
  if (points.length === 0) return <EmptyChart message='Não há dados suficientes para este gráfico.' />;
  const width = 680,
    height = 275,
    left = 58,
    right = 18,
    top = 24,
    bottom = 54;
  const plotWidth = width - left - right,
    plotHeight = height - top - bottom;
  const xMin = Math.min(...points.map((point) => point.x)),
    xMax = Math.max(...points.map((point) => point.x));
  const rawMin = Math.min(...points.map((point) => point.value)),
    rawMax = Math.max(...points.map((point) => point.value));
  const padding = rawMin === rawMax ? Math.max(1, Math.abs(rawMin) * 0.1) : (rawMax - rawMin) * 0.12;
  const yMin = rawMin - padding,
    yMax = rawMax + padding;
  const xPosition = (value: number) =>
    left + (xMax === xMin ? plotWidth / 2 : ((value - xMin) / (xMax - xMin)) * plotWidth);
  const yPosition = (value: number) => top + ((yMax - value) / (yMax - yMin)) * plotHeight;
  const yTicks = Array.from({ length: 5 }, (_, index) => yMin + ((yMax - yMin) * index) / 4).reverse();
  const xTicks = xMin === xMax ? [xMin] : Array.from({ length: 5 }, (_, index) => xMin + ((xMax - xMin) * index) / 4);
  const xTickLabel = (tick: number) =>
    xTickFormatter?.(tick) ??
    points.reduce((closest, point) => (Math.abs(point.x - tick) < Math.abs(closest.x - tick) ? point : closest)).label;
  const axisValue = (value: number) => `${formatNumber(value, percent ? 2 : 0)}${percent ? '%' : ''}`;
  return (
    <Box
      component='svg'
      viewBox={`0 0 ${width} ${height}`}
      role='img'
      aria-label={`${yLabel} por ${xLabel}`}
      sx={{ display: 'block', width: '100%', height: 'auto' }}
    >
      {yTicks.map((tick) => (
        <g key={`y-${tick}`}>
          <line
            x1={left}
            x2={width - right}
            y1={yPosition(tick)}
            y2={yPosition(tick)}
            stroke='currentColor'
            opacity={0.12}
            strokeDasharray='3 4'
          />
          <text x={left - 8} y={yPosition(tick) + 4} textAnchor='end' fontSize='11' fill='currentColor' opacity={0.72}>
            {axisValue(tick)}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <g key={`x-${tick}`}>
          <line
            x1={xPosition(tick)}
            x2={xPosition(tick)}
            y1={top}
            y2={top + plotHeight}
            stroke='currentColor'
            opacity={0.08}
            strokeDasharray='3 4'
          />
          <text
            x={xPosition(tick)}
            y={top + plotHeight + 18}
            textAnchor='middle'
            fontSize='11'
            fill='currentColor'
            opacity={0.72}
          >
            {xTickLabel(tick)}
          </text>
        </g>
      ))}
      <line x1={left} x2={left} y1={top} y2={top + plotHeight} stroke='currentColor' opacity={0.45} />
      <line
        x1={left}
        x2={width - right}
        y1={top + plotHeight}
        y2={top + plotHeight}
        stroke='currentColor'
        opacity={0.45}
      />
      {series.map((item) => (
        <g key={item.label}>
          <path
            d={item.points
              .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xPosition(point.x)} ${yPosition(point.value)}`)
              .join(' ')}
            fill='none'
            stroke={item.color}
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          {item.points.map((point) => (
            <circle
              key={`${item.label}-${point.x}`}
              cx={xPosition(point.x)}
              cy={yPosition(point.value)}
              r='4'
              fill={item.color}
            >
              <title>{`${item.label}: ${point.label} · ${axisValue(point.value)}`}</title>
            </circle>
          ))}
        </g>
      ))}
      <text
        x={left + plotWidth / 2}
        y={height - 8}
        textAnchor='middle'
        fontSize='11'
        fill='currentColor'
        opacity={0.78}
      >
        {xLabel}
      </text>
      <text
        x='14'
        y={top + plotHeight / 2}
        textAnchor='middle'
        fontSize='11'
        fill='currentColor'
        opacity={0.78}
        transform={`rotate(-90 14 ${top + plotHeight / 2})`}
      >
        {yLabel}
      </text>
    </Box>
  );
}

function CashFlowChart({ series }: { series: ChartSeries[] }) {
  const points = series.flatMap((item) => item.points);
  if (points.length === 0) {
    return <EmptyChart message='A agenda de pagamentos ainda não está disponível para os títulos selecionados.' />;
  }
  const width = 680,
    height = 275,
    left = 70,
    right = 18,
    top = 24,
    bottom = 54;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const xMin = Math.min(...points.map((point) => point.x));
  const xMax = Math.max(...points.map((point) => point.x));
  const yMax = Math.max(1, ...points.map((point) => point.value)) * 1.12;
  const hasNormalizedValues = points.some((point) => point.unit === 'NORMALIZED');
  const valueLabel = hasNormalizedValues ? 'Valor normalizado' : 'Valor do fluxo (R$)';
  const formatFlowValue = (value: number) => (hasNormalizedValues ? formatNumber(value, 2) : formatPrice(value));
  const xPosition = (value: number) =>
    left + (xMax === xMin ? plotWidth / 2 : ((value - xMin) / (xMax - xMin)) * plotWidth);
  const yPosition = (value: number) => top + ((yMax - value) / yMax) * plotHeight;
  const yTicks = Array.from({ length: 5 }, (_, index) => (yMax * (4 - index)) / 4);
  const xTicks = xMin === xMax ? [xMin] : Array.from({ length: 5 }, (_, index) => xMin + ((xMax - xMin) * index) / 4);
  const xTickLabel = (tick: number) =>
    new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(tick));
  const seriesWithPoints = series.filter((item) => item.points.length > 0);
  const isDenseSchedule = points.length > 36;
  const isVeryDenseSchedule = points.length > 120;
  const barWidth = Math.min(12, Math.max(4, 18 / seriesWithPoints.length));
  return (
    <Box>
      <Box
        component='svg'
        viewBox={`0 0 ${width} ${height}`}
        role='img'
        aria-label='Valores dos pagamentos futuros por data'
        sx={{ display: 'block', width: '100%', height: 'auto' }}
      >
        {yTicks.map((tick) => (
          <g key={`cashflow-y-${tick}`}>
            <line
              x1={left}
              x2={width - right}
              y1={yPosition(tick)}
              y2={yPosition(tick)}
              stroke='currentColor'
              opacity={0.12}
              strokeDasharray='3 4'
            />
            <text
              x={left - 8}
              y={yPosition(tick) + 4}
              textAnchor='end'
              fontSize='11'
              fill='currentColor'
              opacity={0.72}
            >
              {formatFlowValue(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <g key={`cashflow-x-${tick}`}>
            <line
              x1={xPosition(tick)}
              x2={xPosition(tick)}
              y1={top}
              y2={top + plotHeight}
              stroke='currentColor'
              opacity={0.08}
              strokeDasharray='3 4'
            />
            <text
              x={xPosition(tick)}
              y={top + plotHeight + 18}
              textAnchor='middle'
              fontSize='11'
              fill='currentColor'
              opacity={0.72}
            >
              {xTickLabel(tick)}
            </text>
          </g>
        ))}
        <line x1={left} x2={left} y1={top} y2={top + plotHeight} stroke='currentColor' opacity={0.45} />
        <line
          x1={left}
          x2={width - right}
          y1={top + plotHeight}
          y2={top + plotHeight}
          stroke='currentColor'
          opacity={0.45}
        />
        {seriesWithPoints.map((item, seriesIndex) =>
          item.points.map((point) => {
            const offset = (seriesIndex - (seriesWithPoints.length - 1) / 2) * (barWidth + 2);
            const dateLabel = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone: 'UTC' }).format(
              new Date(point.x),
            );
            const x = xPosition(point.x) + offset;
            const y = yPosition(point.value);
            return isDenseSchedule ? (
              <g key={`${item.label}-${point.x}-${point.label}`}>
                {!isVeryDenseSchedule ? (
                  <line x1={x} x2={x} y1={top + plotHeight} y2={y} stroke={item.color} strokeWidth='1' opacity={0.35} />
                ) : null}
                <circle
                  cx={x}
                  cy={y}
                  r={isVeryDenseSchedule ? 1.75 : 2.25}
                  fill={item.color}
                  stroke='currentColor'
                  strokeOpacity={0.2}
                  strokeWidth='0.75'
                >
                  <title>{`${item.label}: ${point.label} · ${dateLabel} · ${formatFlowValue(point.value)}`}</title>
                </circle>
              </g>
            ) : (
              <rect
                key={`${item.label}-${point.x}-${point.label}`}
                x={x - barWidth / 2}
                y={y}
                width={barWidth}
                height={Math.max(2, top + plotHeight - y)}
                rx='2'
                fill={item.color}
              >
                <title>{`${item.label}: ${point.label} · ${dateLabel} · ${formatFlowValue(point.value)}`}</title>
              </rect>
            );
          }),
        )}
        <text
          x={left + plotWidth / 2}
          y={height - 8}
          textAnchor='middle'
          fontSize='11'
          fill='currentColor'
          opacity={0.78}
        >
          Data do pagamento (mês/ano)
        </text>
        <text
          x='14'
          y={top + plotHeight / 2}
          textAnchor='middle'
          fontSize='11'
          fill='currentColor'
          opacity={0.78}
          transform={`rotate(-90 14 ${top + plotHeight / 2})`}
        >
          {valueLabel}
        </text>
      </Box>
      <Stack direction='row' spacing={1.25} flexWrap='wrap' sx={{ mt: 0.25, px: 1 }}>
        {seriesWithPoints.map((item) => (
          <Stack key={item.label} direction='row' spacing={0.5} alignItems='center'>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
            <Typography variant='caption' color='text.secondary'>
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {isDenseSchedule ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5, px: 1 }}>
          Cada ponto representa um pagamento. Passe o cursor sobre os pontos para consultar a data e o valor.
        </Typography>
      ) : null}
    </Box>
  );
}

function ScatterChart({ series }: { series: ChartSeries[] }) {
  const points = series.flatMap((item) => item.points);
  if (points.length === 0) return <EmptyChart message='Duração ou taxa indisponível para os títulos selecionados.' />;
  const width = 680,
    height = 275,
    left = 58,
    right = 18,
    top = 24,
    bottom = 54;
  const plotWidth = width - left - right,
    plotHeight = height - top - bottom;
  const xMax = Math.max(1, ...points.map((point) => point.x)) * 1.12;
  const yMin = Math.min(0, ...points.map((point) => point.value)) - 1,
    yMax = Math.max(1, ...points.map((point) => point.value)) * 1.15;
  const xPosition = (value: number) => left + (value / xMax) * plotWidth,
    yPosition = (value: number) => top + ((yMax - value) / (yMax - yMin)) * plotHeight;
  const scatterPoints = series.flatMap((item) => item.points.map((point) => ({ ...point, color: item.color })));
  return (
    <Box
      component='svg'
      viewBox={`0 0 ${width} ${height}`}
      role='img'
      aria-label='Taxa por duração dos títulos'
      sx={{ display: 'block', width: '100%', height: 'auto' }}
    >
      {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
        const value = yMin + (yMax - yMin) * (1 - fraction);
        return (
          <g key={fraction}>
            <line
              x1={left}
              x2={width - right}
              y1={top + plotHeight * fraction}
              y2={top + plotHeight * fraction}
              stroke='currentColor'
              opacity={0.12}
              strokeDasharray='3 4'
            />
            <text
              x={left - 8}
              y={top + plotHeight * fraction + 4}
              textAnchor='end'
              fontSize='11'
              fill='currentColor'
              opacity={0.72}
            >
              {formatNumber(value, 1)}%
            </text>
          </g>
        );
      })}
      <line x1={left} x2={left} y1={top} y2={top + plotHeight} stroke='currentColor' opacity={0.45} />
      <line
        x1={left}
        x2={width - right}
        y1={top + plotHeight}
        y2={top + plotHeight}
        stroke='currentColor'
        opacity={0.45}
      />
      {scatterPoints.map((point, index) => {
        const nearbyPoints = scatterPoints.filter((other) => Math.abs(other.x - point.x) < 0.15);
        const nearbyIndex = scatterPoints.slice(0, index).filter((other) => Math.abs(other.x - point.x) < 0.15).length;
        const labelOffset = (nearbyIndex - (nearbyPoints.length - 1) / 2) * 18;
        const labelY = Math.min(top + plotHeight - 4, Math.max(top + 12, yPosition(point.value) + labelOffset));
        const labelX = xPosition(point.x) + 10;
        return (
          <g key={`${point.label}-${point.x}-${index}`}>
            <circle cx={xPosition(point.x)} cy={yPosition(point.value)} r='6' fill={point.color} />
            <text x={labelX} y={labelY} fontSize='10.5' fill='currentColor'>
              {point.label}
            </text>
            <title>{`${point.label}: ${formatNumber(point.value, 2)}% · ${formatNumber(point.x, 2)} anos`}</title>
          </g>
        );
      })}
      <text
        x={left + plotWidth / 2}
        y={height - 8}
        textAnchor='middle'
        fontSize='11'
        fill='currentColor'
        opacity={0.78}
      >
        Duração (anos)
      </text>
      <text
        x='14'
        y={top + plotHeight / 2}
        textAnchor='middle'
        fontSize='11'
        fill='currentColor'
        opacity={0.78}
        transform={`rotate(-90 14 ${top + plotHeight / 2})`}
      >
        Taxa (% a.a.)
      </text>
    </Box>
  );
}

function BenchmarkStrip({ benchmarks }: { benchmarks: Benchmarks }) {
  const items = [
    { label: 'Selic atual', point: benchmarks.selic, unit: '% a.a.' },
    { label: 'IPCA atual', point: benchmarks.ipca, unit: '% no mês' },
    { label: 'IGP-M atual', point: benchmarks.igpm, unit: '% no mês' },
  ];
  return (
    <Paper variant='outlined' sx={{ p: 1.25, bgcolor: 'action.hover' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        divider={<Divider orientation='vertical' flexItem />}
      >
        {items.map((item) => (
          <Stack key={item.label} direction='row' spacing={1} alignItems='center' sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'background.paper',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 800 }}>
                %
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='caption' color='text.secondary'>
                {item.label}
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 750 }}>
                {item.point ? `${formatNumber(item.point.value, 2)} ${item.unit}` : 'Sem dado disponível'}
              </Typography>
            </Box>
            <Button
              component={AppLink}
              href='/mercado'
              size='small'
              endIcon={<ExternalLink size={13} />}
              sx={{ ml: 'auto', whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Ver no Mercado
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

type Field = {
  key: string;
  label: string;
  description: string;
  value: (row: Row) => ReactNode;
  numeric?: (row: Row) => number | null;
};

export default function FixedIncomeComparison() {
  const appColors = useAppColors();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [history, setHistory] = useState<History[]>([]);
  const [cashFlows, setCashFlows] = useState<{ id: string; flows: CashFlowPoint[] }[]>([]);
  const [benchmarks, setBenchmarks] = useState<Benchmarks>({ selic: null, ipca: null, igpm: null });
  const [error, setError] = useState(false);
  const [shared, setShared] = useState(false);
  const [comparisonIds, setComparisonIds] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ charts: false, additional: false });

  useEffect(() => {
    setComparisonIds(new URLSearchParams(window.location.search).get('ids') ?? '');
  }, []);

  useEffect(() => {
    if (comparisonIds === null) return;
    const controller = new AbortController();
    fetch(`/api/renda-fixa/comparacao?ids=${encodeURIComponent(comparisonIds)}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((result) =>
        result.ok
          ? (result.json() as Promise<{
              data: Row[];
              cashFlows?: {
                id: string;
                date: string | null;
                flowType: string;
                amount: number | null;
                indexer: string | null;
                projected: boolean;
              }[];
              history?: History[];
              benchmarks?: Benchmarks;
            }>)
          : Promise.reject(new Error('comparison')),
      )
      .then((payload) => {
        setRows(payload.data);
        const groupedCashFlows = payload.cashFlows?.reduce<Record<string, CashFlowPoint[]>>((grouped, flow) => {
          (grouped[flow.id] ??= []).push({
            date: flow.date,
            flowType: flow.flowType,
            amount: flow.amount,
            indexer: flow.indexer,
            projected: flow.projected,
          });
          return grouped;
        }, {});
        setCashFlows(Object.entries(groupedCashFlows ?? {}).map(([id, flows]) => ({ id, flows })));
        setHistory(payload.history ?? []);
        setBenchmarks(payload.benchmarks ?? { selic: null, ipca: null, igpm: null });
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(true);
      });
    return () => controller.abort();
  }, [comparisonIds]);

  const toggle = (key: string) => setCollapsed((current) => ({ ...current, [key]: !current[key] }));
  const updateComparison = (ids: string[]) => {
    const nextIds = ids.join(',');
    setComparisonIds(nextIds);
    window.history.replaceState(null, '', `${window.location.pathname}?ids=${encodeURIComponent(nextIds)}`);
  };
  const share = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      setShared(true);
    } catch {
      setShared(false);
    }
  };
  const colors = appColors.modules.comparadorSeries;
  const historyById = useMemo(() => new Map(history.map((item) => [item.id, item.points])), [history]);
  const cashFlowsById = useMemo(() => new Map(cashFlows.map((item) => [item.id, item.flows])), [cashFlows]);

  const fields = useMemo(() => {
    if (!rows) return null;
    const basic: Field[] = [
      {
        key: 'name',
        label: 'Nome completo',
        description: 'Nome publicado para identificar o título na fonte oficial.',
        value: friendlyTitle,
      },
      {
        key: 'code',
        label: 'Código',
        description: 'Identificação usada pela fonte para localizar o título e acompanhar suas cotações.',
        value: instrumentCode,
      },
      {
        key: 'titleType',
        label: 'Tipo de título',
        description:
          'Categoria do título, como Prefixado, IPCA+ ou Selic. Ela ajuda a contextualizar a forma de remuneração.',
        value: (row) => formatEnum(row.titleType),
      },
      {
        key: 'indexer',
        label: 'Indexador',
        description:
          'Referência que influencia a remuneração do título, como Selic, IPCA ou IGP-M. “Sem indexador” indica uma taxa prefixada.',
        value: (row) => formatEnum(row.indexer),
      },
      {
        key: 'flow',
        label: 'Fluxo',
        description:
          'Mostra quando o título paga: somente no vencimento ou também em pagamentos intermediários, como juros semestrais.',
        value: (row) =>
          `${formatEnum(row.cashFlowType)}${row.paymentFrequency ? ` · ${formatEnum(row.paymentFrequency)}` : ''}`,
      },
      {
        key: 'maturity',
        label: 'Vencimento',
        description: 'Data em que termina o título e ocorre o pagamento final previsto na fonte.',
        value: (row) => formatDate(row.maturityDate),
      },
      {
        key: 'conversion',
        label: 'Conversão / início dos pagamentos',
        description:
          'Data em que começam as amortizações mensais do Educa+ ou do RendA+. Para os demais títulos, este campo não se aplica.',
        value: (row) => formatDate(row.conversionDate),
      },
      {
        key: 'days',
        label: 'Prazo restante',
        description:
          'Quantidade aproximada de dias até o vencimento, contada a partir da data de referência da cotação.',
        value: (row) => `${formatNumber(row.daysToMaturity, 0)} dias`,
      },
    ];
    const remuneration: Field[] = [
      {
        key: 'rate',
        label: 'Taxa de remuneração (% a.a.)',
        description:
          'Taxa anual informada na cotação oficial. Em títulos indexados, o valor é mostrado junto do indexador correspondente.',
        value: formatRate,
        numeric: (row) => row.sellRateMorning,
      },
      {
        key: 'pu',
        label: 'PU de referência',
        description:
          'Preço unitário de referência publicado para a cotação. É o valor de uma unidade do título na data indicada.',
        value: (row) => formatPrice(row.sellPuMorning),
      },
      {
        key: 'quoteDate',
        label: 'Data da cotação',
        description: 'Data em que a taxa e o preço apresentados foram observados e publicados.',
        value: (row) => formatDate(row.quoteDate),
      },
      {
        key: 'source',
        label: 'Fonte',
        description: 'Origem oficial dos valores apresentados nesta comparação.',
        value: (row) => formatEnum(row.source),
      },
    ];
    const risk: Field[] = [
      {
        key: 'duration',
        label: 'Duração (anos)',
        description:
          'Indica, em anos, o prazo médio ponderado dos pagamentos do título. Ajuda a entender quanto tempo o valor econômico fica exposto às mudanças nas taxas. Para títulos sem cupons, coincide aproximadamente com o prazo até o vencimento; para títulos com pagamentos intermediários, depende de todos os fluxos de caixa.',
        value: (row) => (row.durationYears === null ? <MissingValue /> : `${formatNumber(row.durationYears, 2)} anos`),
        numeric: (row) => row.durationYears,
      },
      {
        key: 'modifiedDuration',
        label: 'Duração modificada (anos)',
        description:
          'Aproxima a variação percentual do preço para uma mudança de 1 ponto percentual na taxa de rendimento. É uma sensibilidade local, não uma previsão de retorno. O valor exige que os fluxos de caixa do título sejam precificados pela metodologia correspondente.',
        value: (row) =>
          row.modifiedDurationYears === null ? <MissingValue /> : `${formatNumber(row.modifiedDurationYears, 2)} anos`,
        numeric: (row) => row.modifiedDurationYears,
      },
      {
        key: 'dv01',
        label: 'DV01 (R$)',
        description:
          'Estima quanto o preço unitário tende a mudar, em reais, diante de uma variação de 1 ponto-base na taxa (0,01%). Mede impacto monetário aproximado e não representa ganho ou perda observado.',
        value: (row) => (row.dv01 === null ? <MissingValue /> : formatDv01(row.dv01)),
        numeric: (row) => row.dv01,
      },
      {
        key: 'convexity',
        label: 'Convexidade',
        description:
          'Mede a curvatura da relação entre preço e taxa. Junto com a duração modificada, melhora a aproximação para pequenos choques de taxa; não representa retorno futuro.',
        value: (row) => (row.convexity === null ? <MissingValue /> : formatNumber(row.convexity, 4)),
        numeric: (row) => row.convexity,
      },
      {
        key: 'up25',
        label: 'Choque +25 bps (%)',
        description:
          'Variação percentual estimada do preço se a taxa de risco usada na precificação subir 25 pontos-base (0,25%). O cálculo deve considerar a estrutura de pagamentos do título; para títulos com cupons, a ausência indica que essa metodologia ainda não está disponível, e não que o risco seja zero.',
        value: (row) =>
          row.sensitivityUp25BpsPct === null ? <MissingValue /> : `${formatNumber(row.sensitivityUp25BpsPct, 2)}%`,
        numeric: (row) => row.sensitivityUp25BpsPct,
      },
      {
        key: 'down25',
        label: 'Choque -25 bps (%)',
        description:
          'Variação percentual estimada do preço se a taxa de risco usada na precificação cair 25 pontos-base (0,25%). Como a relação entre preço e taxa é curva, o resultado não precisa ser exatamente o oposto do choque de alta.',
        value: (row) =>
          row.sensitivityDown25BpsPct === null ? <MissingValue /> : `${formatNumber(row.sensitivityDown25BpsPct, 2)}%`,
        numeric: (row) => row.sensitivityDown25BpsPct,
      },
    ];
    const liquidity: Field[] = [
      {
        key: 'liquidity',
        label: 'Liquidez (compra)',
        description:
          'Prazo indicado para a liquidação da compra, conforme o cadastro do título. Isso não é uma promessa de preço de venda.',
        value: liquidityLabel,
      },
      {
        key: 'tax',
        label: 'Tributação',
        description:
          'Regra de imposto indicada para o título. O valor efetivo pode depender do prazo e da situação do investidor.',
        value: (row) => formatEnum(row.taxRegime),
      },
    ];
    const additional: Field[] = [
      {
        key: 'riskFactor',
        label: 'Fator de risco',
        description:
          'Indica qual referência de mercado é alterada nos choques de taxa. Em títulos indexados, a taxa real é separada da inflação; no Tesouro Selic, o choque recai sobre o spread publicado. É uma classificação informativa, não uma nota de risco.',
        value: (row) => formatEnum(row.riskFactor),
      },
      {
        key: 'sourceAsOf',
        label: 'Atualização da fonte',
        description: 'Data mais recente de atualização dos dados recebidos da fonte oficial.',
        value: (row) => formatDate(row.sourceAsOf),
      },
    ];
    return { basic, remuneration, risk, liquidity, additional };
  }, [rows]);

  const chartSeries = useMemo(() => {
    if (!rows) return { marking: [], history: [], cashflow: [], duration: [] };
    const titleSeries = (row: Row, index: number): ChartSeries => ({
      label: compactChartTitle(row),
      color: colors[index % colors.length],
      points: [],
    });
    const marking = rows.map((row, index) => {
      const hasSensitivity = row.sensitivityDown25BpsPct !== null || row.sensitivityUp25BpsPct !== null;
      return {
        ...titleSeries(row, index),
        points: !hasSensitivity
          ? []
          : [
              row.sensitivityDown25BpsPct === null
                ? null
                : { x: -25, label: '-25 bps', value: row.sensitivityDown25BpsPct },
              { x: 0, label: '0 bps', value: 0 },
              row.sensitivityUp25BpsPct === null ? null : { x: 25, label: '+25 bps', value: row.sensitivityUp25BpsPct },
            ].filter((point): point is ChartPoint => point !== null),
      };
    });
    const historical = rows.map((row, index) => ({
      ...titleSeries(row, index),
      points: (historyById.get(row.id) ?? []).flatMap((point) =>
        point.sellPuBase100 === null || point.date === null
          ? []
          : [
              {
                x: Date.parse(`${point.date}T12:00:00Z`),
                label: new Intl.DateTimeFormat('pt-BR', { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(
                  new Date(`${point.date}T12:00:00Z`),
                ),
                value: point.sellPuBase100,
              },
            ],
      ),
    }));
    const duration = rows.map((row, index) => ({
      ...titleSeries(row, index),
      points:
        row.durationYears === null || row.sellRateMorning === null
          ? []
          : [{ x: row.durationYears, label: compactChartTitle(row), value: row.sellRateMorning }],
    }));
    const cashflow = rows.map((row, index) => ({
      ...titleSeries(row, index),
      points: (cashFlowsById.get(row.id) ?? []).flatMap((flow) =>
        flow.date === null || flow.amount === null
          ? []
          : [
              {
                x: Date.parse(`${flow.date}T12:00:00Z`),
                label: formatEnum(flow.flowType),
                value: flow.amount,
                unit: isIndexedFlow(flow.indexer) ? ('NORMALIZED' as const) : ('BRL' as const),
              },
            ],
      ),
    }));
    return { marking, history: historical, cashflow, duration };
  }, [cashFlowsById, colors, historyById, rows]);

  if (error)
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 6 }}>
        <Alert severity='error'>Não foi possível carregar a comparação.</Alert>
      </Box>
    );
  if (!rows || !fields)
    return (
      <Stack alignItems='center' sx={{ py: 10 }}>
        <CircularProgress aria-label='Carregando comparação' />
      </Stack>
    );
  if (rows.length < 2) {
    return (
      <>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 6 }}>
          <Paper variant='outlined' sx={{ p: { xs: 2.5, md: 4 }, borderStyle: 'dashed', textAlign: 'center' }}>
            <Typography variant='overline' color='primary' fontWeight={900} letterSpacing={1.4}>
              COMPARAÇÃO
            </Typography>
            <Typography component='h1' variant='h5' sx={{ mt: 0.5, fontWeight: 850 }}>
              Comparador de Renda Fixa
            </Typography>
            <Typography color='text.secondary' sx={{ mt: 1, maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}>
              Escolha de dois a quatro títulos do Tesouro Direto para colocar taxas, prazos, fluxos de caixa e risco
              lado a lado.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent='center' sx={{ mt: 2.5 }}>
              <Button variant='contained' onClick={() => setPickerOpen(true)} startIcon={<ListPlus size={15} />}>
                Escolher títulos
              </Button>
              <Button component={AppLink} href='/renda-fixa' variant='outlined' startIcon={<ArrowLeft size={16} />}>
                Ver catálogo
              </Button>
            </Stack>
            {rows.length === 1 ? (
              <Alert severity='info' sx={{ mt: 2.5, textAlign: 'left' }}>
                Falta um título: selecione mais um para carregar a comparação.
              </Alert>
            ) : null}
          </Paper>
        </Box>
        <FixedIncomeCompareDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          selectedIds={(comparisonIds ?? '').split(',').filter(Boolean)}
          onSelectedIdsChange={updateComparison}
          currentTitles={rows.map(friendlyTitle).join(', ')}
        />
      </>
    );
  }

  const comparable = new Set(rows.map((row) => row.indexer)).size === 1;
  const gridColumns = `minmax(190px, 1.15fr) repeat(${rows.length}, minmax(205px, 1fr))`;
  const sections = [
    { key: 'basics', title: 'Dados básicos', fields: fields.basic },
    { key: 'remuneration', title: 'Remuneração e preços', fields: fields.remuneration },
    { key: 'risk', title: 'Risco e sensibilidade', fields: fields.risk },
    { key: 'liquidity', title: 'Liquidez e tributação', fields: fields.liquidity },
  ];
  const riskFactors = Array.from(new Set(rows.map((row) => row.riskFactor).filter(Boolean)));
  const hasIndexedCashFlows = chartSeries.cashflow.some((item) =>
    item.points.some((point) => point.unit === 'NORMALIZED'),
  );
  const renderFieldRows = (fieldList: Field[], sectionKey: string) =>
    fieldList.map((field) => {
      const numericValues = field.numeric
        ? rows.map(field.numeric).filter((value): value is number => value !== null)
        : [];
      const maximum = numericValues.length > 0 ? Math.max(...numericValues) : null;
      return (
        <Box key={`${sectionKey}-${field.key}`} sx={{ display: 'contents' }}>
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 1,
              minHeight: 40,
            }}
          >
            <IndicatorLabel label={field.label} description={field.description} />
          </Box>
          {rows.map((row, index) => {
            const numeric = field.numeric?.(row) ?? null;
            const progress =
              maximum !== null && numeric !== null && maximum > 0 ? Math.max(3, (numeric / maximum) * 100) : null;
            return (
              <Box
                key={`${field.key}-${row.id}`}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  px: 1.5,
                  py: 1,
                  minWidth: 0,
                  color: 'text.primary',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: field.key === 'rate' || field.key === 'duration' ? 700 : 500,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {field.value(row)}
                </Typography>
                {progress !== null ? (
                  <LinearProgress
                    variant='determinate'
                    value={progress}
                    sx={{
                      mt: 0.65,
                      height: 6,
                      borderRadius: 6,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { bgcolor: colors[index % colors.length], borderRadius: 6 },
                    }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      );
    });

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1, md: 2 }, py: { xs: 2, md: 3 } }}>
      <Paper variant='outlined' sx={{ overflow: 'hidden', bgcolor: 'background.default' }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent='space-between'
            alignItems={{ lg: 'flex-start' }}
            gap={2}
          >
            <Box>
              <Button
                component={AppLink}
                href='/renda-fixa'
                startIcon={<ArrowLeft size={15} />}
                size='small'
                sx={{ mb: 1 }}
              >
                Voltar ao catálogo
              </Button>
              <Typography
                variant='overline'
                color='primary'
                sx={{ display: 'block', fontWeight: 800, letterSpacing: 1.4 }}
              >
                COMPARAÇÃO
              </Typography>
              <Typography component='h1' variant='h3' sx={{ fontWeight: 800, letterSpacing: -1, lineHeight: 1.05 }}>
                Comparador de Renda Fixa{' '}
                <Tooltip title='Compare apenas dados publicados; não é uma recomendação.' arrow>
                  <Box
                    component='span'
                    sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 0.5, color: 'text.secondary' }}
                  >
                    <CircleHelp size={17} />
                  </Box>
                </Tooltip>
              </Typography>
              <Typography color='text.secondary' sx={{ mt: 0.75 }}>
                Compare características, riscos e comportamento dos títulos do Tesouro Direto.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexShrink: 0 }}>
              <Button variant='outlined' onClick={() => setPickerOpen(true)} startIcon={<ListPlus size={15} />}>
                Escolher títulos
              </Button>
              <Button component={AppLink} href='/renda-fixa' variant='outlined' startIcon={<X size={15} />}>
                Limpar comparação
              </Button>
              <Button variant='contained' onClick={share} startIcon={<Share2 size={15} />}>
                Compartilhar
              </Button>
            </Stack>
          </Stack>
          {!comparable ? (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Os títulos usam indexadores diferentes. As taxas aparecem lado a lado, mas não devem ser tratadas como
              equivalentes.
            </Alert>
          ) : null}
        </Box>
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 2, overflowX: 'auto' }}>
          <Box sx={{ minWidth: `${190 + rows.length * 205}px`, display: 'grid', gridTemplateColumns: gridColumns }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRight: 0,
                p: 1.5,
                minHeight: 100,
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant='body2' sx={{ fontWeight: 750 }}>
                Títulos
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                ({rows.length} de 4)
              </Typography>
            </Box>
            {rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRight: 0,
                  p: 1.5,
                  minWidth: 0,
                  bgcolor: 'background.paper',
                  '&:last-child': { borderRight: '1px solid', borderColor: 'divider' },
                }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={0.5}>
                  <Chip
                    size='small'
                    label={titleTag(row)}
                    color={row.indexer === 'IPCA' ? 'secondary' : row.indexer === 'SELIC' ? 'success' : 'primary'}
                    sx={{ fontWeight: 700, maxWidth: 'calc(100% - 28px)' }}
                  />
                  <IconButton
                    component={AppLink}
                    href='/renda-fixa'
                    size='small'
                    aria-label={`Remover ${friendlyTitle(row)} da comparação`}
                    sx={{ p: 0.25 }}
                  >
                    <X size={16} />
                  </IconButton>
                </Stack>
                <Typography
                  component={AppLink}
                  href={`/ativo/${encodeURIComponent(row.idCem)}`}
                  variant='body2'
                  sx={{
                    display: 'block',
                    mt: 1,
                    fontWeight: 750,
                    color: 'text.primary',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    overflowWrap: 'anywhere',
                  }}
                >
                  {friendlyTitle(row)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {instrumentCode(row)}
                </Typography>
              </Box>
            ))}
            {sections.map((section) => (
              <Box key={section.key} sx={{ display: 'contents' }}>
                <SectionHeader
                  id={`section-${section.key}`}
                  title={section.title}
                  count={section.fields.length}
                  collapsed={Boolean(collapsed[section.key])}
                  onToggle={() => toggle(section.key)}
                />
                {!collapsed[section.key] ? (
                  <Box id={`section-${section.key}`} sx={{ display: 'contents' }}>
                    {renderFieldRows(section.fields, section.key)}
                  </Box>
                ) : null}
              </Box>
            ))}
            <Box sx={{ display: 'contents' }}>
              <SectionHeader
                id='section-charts'
                title='Gráficos e simulações'
                count={4}
                collapsed={Boolean(collapsed.charts)}
                onToggle={() => toggle('charts')}
              />
              {!collapsed.charts ? (
                <Box id='section-charts' sx={{ gridColumn: '1 / -1', p: { xs: 1, md: 1.5 }, bgcolor: 'action.hover' }}>
                  <Alert severity='info' icon={<Info size={17} />} sx={{ mb: 1.5, py: 0.5 }}>
                    <Stack spacing={0.45}>
                      <Typography variant='caption' fontWeight={750}>
                        Como ler os choques
                      </Typography>
                      <Typography variant='caption'>
                        Os pontos mostram a variação estimada do PU quando o fator de risco de cada título muda 25
                        pontos-base. Isso é uma simulação de marcação a mercado, não uma previsão de retorno.
                      </Typography>
                      <Stack direction='row' spacing={0.6} useFlexGap flexWrap='wrap' alignItems='center'>
                        <Typography variant='caption' color='text.secondary'>
                          Bases nesta comparação:
                        </Typography>
                        {riskFactors.map((factor) => (
                          <Tooltip key={factor} title={riskFactorDescription(factor)} arrow>
                            <Chip size='small' variant='outlined' label={formatEnum(factor)} sx={{ height: 22 }} />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Stack>
                  </Alert>
                  <BenchmarkStrip benchmarks={benchmarks} />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.25,
                      mt: 1.25,
                    }}
                  >
                    <ChartCard
                      id='chart-marking'
                      title='Variação estimada do preço'
                      subtitle='Resposta publicada para choques de ±25 pontos-base no fator de risco do título.'
                    >
                      <LineChart
                        series={chartSeries.marking}
                        xLabel='Choque na taxa (pontos-base)'
                        yLabel='Variação do preço (%)'
                        percent
                        xTickFormatter={(value) => `${value > 0 ? '+' : ''}${formatNumber(value, 0)} bps`}
                      />
                    </ChartCard>
                    <ChartCard
                      id='chart-history'
                      title='Evolução do PU (base 100)'
                      subtitle='PU de venda normalizado na primeira observação histórica disponível.'
                    >
                      <LineChart
                        series={chartSeries.history}
                        xLabel='Data da cotação (mês/ano)'
                        yLabel='PU normalizado (base 100)'
                      />
                    </ChartCard>
                    <ChartCard
                      id='chart-cashflow'
                      title='Fluxo de caixa'
                      subtitle={
                        hasIndexedCashFlows
                          ? 'Agenda de pagamentos em valores normalizados; títulos indexados não representam recebimentos nominais futuros.'
                          : 'Datas e valores dos pagamentos futuros previstos para os títulos.'
                      }
                    >
                      <CashFlowChart series={chartSeries.cashflow} />
                      {rows.some((row) => row.cashFlowScheduleId === null) ? (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
                          A agenda detalhada está disponível para os títulos com fluxos calculados. Nos demais, o
                          catálogo informa apenas a estrutura de pagamento.
                        </Typography>
                      ) : null}
                    </ChartCard>
                    <ChartCard
                      id='chart-duration'
                      title='Taxa × duração'
                      subtitle='Relação entre a taxa publicada e a duração disponível para os títulos.'
                    >
                      <ScatterChart series={chartSeries.duration} />
                    </ChartCard>
                  </Box>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
                    Dica: passe o cursor sobre os pontos para ver o título e o valor exato.
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <SectionHeader
              id='section-additional'
              title='Métricas adicionais'
              count={fields.additional.length}
              collapsed={Boolean(collapsed.additional)}
              onToggle={() => toggle('additional')}
            />
            {!collapsed.additional ? (
              <Box id='section-additional' sx={{ display: 'contents' }}>
                {renderFieldRows(fields.additional, 'additional')}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          alignItems={{ sm: 'center' }}
          gap={1}
          sx={{ px: { xs: 2, md: 3 }, pb: 2 }}
        >
          <Button
            component={AppLink}
            href='/renda-fixa'
            startIcon={<SlidersHorizontal size={16} />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Adicionar título
          </Button>
          <Alert severity='info' icon={<Info size={18} />} sx={{ py: 0, maxWidth: 620 }}>
            A comparação não calcula retorno futuro nem representa recomendação de investimento. Taxas e preços são
            referências publicadas pelo Tesouro Transparente.
          </Alert>
        </Stack>
      </Paper>
      <FixedIncomeCompareDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedIds={(comparisonIds ?? '').split(',').filter(Boolean)}
        onSelectedIdsChange={updateComparison}
        currentTitles={rows.map(friendlyTitle).join(', ')}
      />
      <Snackbar
        open={shared}
        autoHideDuration={2500}
        onClose={() => setShared(false)}
        message='Link da comparação copiado.'
      />
    </Box>
  );
}
